import { supabase } from "@/integrations/supabase/client";
import { getCurrentEmpresaId } from "@/integrations/supabase/auth-utils";

export interface MedidaConserto {
  id: string;
  pedido_numero: string;
  cliente_id: string;
  data_medida: string;
  tipo_medida: "altura" | "largura" | "profundidade" | "cintura" | "busto" | "manga" | "comprimento" | "personalizada";
  valor: number;
  unidade: "cm" | "polegadas";
  notas?: string;
  data_criacao: string;
}

export interface HistoricoMedidasCliente {
  cliente_id: string;
  medidas: MedidaConserto[];
  ultima_atualizacao: string;
}

/**
 * Sincronizar medidas de um conserto para o histórico do cliente
 */
export async function sincronizarMedidasConserto(
  clienteId: string,
  pedidoNumero: string,
  medidas: Array<{
    tipo: string;
    valor: number;
    unidade: string;
    notas?: string;
  }>
): Promise<{ ok: boolean; error?: string }> {
  try {
    const empresaId = await getCurrentEmpresaId();
    if (!empresaId) {
      return { ok: false, error: "Empresa não identificada" };
    }

    // Buscar histórico de medidas do cliente
    const { data: clienteData, error: clienteError } = await supabase
      .from("customers")
      .select("medidas_historico")
      .eq("id", clienteId)
      .eq("empresa_id", empresaId)
      .single();

    if (clienteError) {
      console.error("Erro ao buscar cliente:", clienteError);
      return { ok: false, error: "Cliente não encontrado" };
    }

    // Preparar novas medidas
    const medidasAtuais = (clienteData?.medidas_historico || []) as any[];
    const novasMedidas = medidas.map((m) => ({
      id: `med_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      pedido_numero: pedidoNumero,
      cliente_id: clienteId,
      data_medida: new Date().toISOString().split("T")[0],
      tipo_medida: m.tipo,
      valor: m.valor,
      unidade: m.unidade,
      notas: m.notas,
      data_criacao: new Date().toISOString(),
    }));

    // Combinar com medidas antigas
    const todasAsMedidas = [...medidasAtuais, ...novasMedidas];

    // Atualizar cliente
    const { error: updateError } = await supabase
      .from("customers")
      .update({
        medidas_historico: todasAsMedidas,
        ultima_atualizacao_medidas: new Date().toISOString(),
      })
      .eq("id", clienteId)
      .eq("empresa_id", empresaId);

    if (updateError) {
      console.error("Erro ao sincronizar medidas:", updateError);
      return { ok: false, error: "Erro ao sincronizar medidas" };
    }

    return { ok: true };
  } catch (error: any) {
    console.error("Erro ao sincronizar medidas:", error);
    return { ok: false, error: error?.message ?? "Erro ao sincronizar medidas" };
  }
}

/**
 * Obter histórico de medidas de um cliente
 */
export async function obterMedidasCliente(clienteId: string): Promise<MedidaConserto[]> {
  try {
    const empresaId = await getCurrentEmpresaId();
    if (!empresaId) {
      return [];
    }

    const { data, error } = await supabase
      .from("customers")
      .select("medidas_historico")
      .eq("id", clienteId)
      .eq("empresa_id", empresaId)
      .single();

    if (error) {
      console.error("Erro ao buscar medidas do cliente:", error);
      return [];
    }

    return (data?.medidas_historico || []) as MedidaConserto[];
  } catch (error) {
    console.error("Erro ao obter medidas do cliente:", error);
    return [];
  }
}

/**
 * Reutilizar medida anterior ao criar novo conserto
 */
export async function reutilizarMedidaPosterior(
  clienteId: string,
  medidaId: string
): Promise<MedidaConserto | null> {
  try {
    const medidas = await obterMedidasCliente(clienteId);
    return medidas.find((m) => m.id === medidaId) || null;
  } catch (error) {
    console.error("Erro ao reutilizar medida:", error);
    return null;
  }
}

/**
 * Sincronizar medidas registradas em atelie_medidas para customers.medidas_historico
 * Expande cada medida em uma entrada separada no array
 */
export async function sincronizarMedidasRegistradas(
  clienteId: string,
  medidasRegistradas: any // Medida da tabela atelie_medidas
): Promise<{ ok: boolean; error?: string }> {
  try {
    const empresaId = await getCurrentEmpresaId();
    if (!empresaId) {
      return { ok: false, error: "Empresa não identificada" };
    }

    // Buscar histórico atual
    const { data: clienteData, error: clienteError } = await supabase
      .from("customers")
      .select("medidas_historico")
      .eq("id", clienteId)
      .eq("empresa_id", empresaId)
      .single();

    if (clienteError) {
      console.error("Erro ao buscar cliente:", clienteError);
      return { ok: false, error: "Cliente não encontrado" };
    }

    // Expandir cada medida como uma entrada separada
    const medidasAtuais = (clienteData?.medidas_historico || []) as any[];
    const novasMedidas: any[] = [];

    // Campos que representam medidas
    const camposMedidas = [
      "busto", "cintura", "quadril", "ombro", "largura_costas", "cava_manga",
      "grossura_braco", "comprimento_manga", "cana_braco", "alca", "pescoco",
      "comprimento", "coxa", "tornozelo", "comprimento_calca"
    ];

    for (const campo of camposMedidas) {
      if (medidasRegistradas[campo] !== null && medidasRegistradas[campo] !== undefined) {
        novasMedidas.push({
          id: `med_${medidasRegistradas.id}_${campo}`,
          pedido_numero: "",
          cliente_id: clienteId,
          data_medida: new Date().toISOString().split("T")[0],
          tipo_medida: campo,
          valor: medidasRegistradas[campo],
          unidade: "cm",
          notas: medidasRegistradas.observacoes || "",
          data_criacao: new Date().toISOString(),
        });
      }
    }

    // Remover medidas antigas deste registro (por ID) e adicionar as novas
    const medidasFiltradas = medidasAtuais.filter(m => !m.id.includes(`med_${medidasRegistradas.id}_`));
    const todasAsMedidas = [...medidasFiltradas, ...novasMedidas];

    // Atualizar cliente
    const { error: updateError } = await supabase
      .from("customers")
      .update({
        medidas_historico: todasAsMedidas,
        ultima_atualizacao_medidas: new Date().toISOString(),
      })
      .eq("id", clienteId)
      .eq("empresa_id", empresaId);

    if (updateError) {
      console.error("Erro ao sincronizar medidas registradas:", updateError);
      return { ok: false, error: "Erro ao sincronizar medidas" };
    }

    return { ok: true };
  } catch (error: any) {
    console.error("Erro ao sincronizar medidas registradas:", error);
    return { ok: false, error: error?.message ?? "Erro ao sincronizar" };
  }
}

/**
 * Deletar medida do histórico
 */
export async function deletarMedidaCliente(
  clienteId: string,
  medidaId: string
): Promise<{ ok: boolean; error?: string }> {
  try {
    const empresaId = await getCurrentEmpresaId();
    if (!empresaId) {
      return { ok: false, error: "Empresa não identificada" };
    }

    const { data: clienteData, error: clienteError } = await supabase
      .from("customers")
      .select("medidas_historico")
      .eq("id", clienteId)
      .eq("empresa_id", empresaId)
      .single();

    if (clienteError) {
      return { ok: false, error: "Cliente não encontrado" };
    }

    const medidasAtuais = (clienteData?.medidas_historico || []) as any[];
    const medidasAtualizadas = medidasAtuais.filter((m) => m.id !== medidaId);

    const { error: updateError } = await supabase
      .from("customers")
      .update({ medidas_historico: medidasAtualizadas })
      .eq("id", clienteId)
      .eq("empresa_id", empresaId);

    if (updateError) {
      return { ok: false, error: "Erro ao deletar medida" };
    }

    return { ok: true };
  } catch (error: any) {
    console.error("Erro ao deletar medida:", error);
    return { ok: false, error: error?.message ?? "Erro ao deletar medida" };
  }
}
